import {LitElement, html, TemplateResult, PropertyValues} from 'lit';
import {ifDefined} from 'lit/directives/if-defined.js';
import {getFormJSON} from '../elements/forms';
import {Analytics} from '../elements/analytics';
import {Auth} from '../elements/auth';
import {Service} from '../elements/service';
import '../elements/billing';
import '../elements/inventory-planner';
import '../elements/product-sync';
import '../elements/validation';

const fields = [
  {
    id: 'title',
    name: 'Product Title',
  },
  {
    id: 'productType',
    name: 'Product Type',
  },
  {
    id: 'productCategory',
    name: 'Product Category',
  }];

/**
 * `setup-page`
 */
export class SetupPage extends LitElement {
  /**
   * Render in the light dom
   *
   * @returns {object} this
   */
  createRenderRoot() {
    return this;
  }

  /**
   * Defined the elements content
   *
   * @returns {TemplateResult} the resulting html template
   */
  render() {
    if (this.isError) {
      return html`<p>Oops there has been an error. If this happens again, please <a href="mailto:jordan@finners.dev">email us</a>.</p>`;
    }

    if (this.isLoading) {
      return html`<p>Loading...</p>`;
    }

    return html`
    <section class="setup">
      <div class="summary">
        <h2>Set Up</h2>
        <p>Set up Peoplevox to allow synchronization. You will only need to do this once.</p>
        <ol>
          <li>Create a User that this app can use in Peoplevox. <br>
            You will need this in the settings below.<br>
            Ensure it has a secure password using more than 15 characters, special characters and numbers.</li>
          <li>Download the following files, in Peoplevox Integration Templates click 'Configure' for each line
            matching the name on the button.</li>
          <li>It is your responsibility to manage GDPR requests in both Shopify and Peoplevox.</li>
        </ol>
      </div>
      <div class="templates">
        <div class="template">
          <a href="/templates/item-types.csv" target="_blank" download="item-types.csv">Download the following file, click
            'Configure' on Integration Template for <b>Item types</b>.</a>
        </div>
        <div class="template">
          <a href="/templates/sales-orders.csv" target="_blank" download="sales-orders.csv">Download the following file,
            click 'Configure' on Integration Template for <b>Sales orders</b>.</a>
        </div>
        <div class="template">
          <a href="/templates/sales-order-items.csv" target="_blank" download="sales-order-items.csv">Download the following
            file, click 'Configure' on Integration Template for <b>Sales order items</b>.</a>
        </div>
        <div class="template">
          <a href="/templates/despatches.csv" target="_blank" download="despatches.csv">Download the following file, click
            'Configure' on Integration Template for <b>Despatches</b>.</a>
        </div>
        <div class="template">
          <a href="/templates/carriers.csv" target="_blank" download="carriers.csv">Download the following file, click
            'Configure' on Integration Template for <b>Carriers</b>.</a>
        </div>
        <div class="template">
          <a href="/templates/service-types.csv" target="_blank" download="service-types.csv">Download the following file,
            click 'Configure' on Integration Template for <b>Service types</b>.</a>
        </div>
        <div class="template">
          <a href="/templates/suppliers.csv" target="_blank" download="suppliers.csv">Download the following file, click
            'Configure' on Integration Template for <b>Suppliers</b>.</a>
        </div>
        <div class="template">
          <a href="/templates/purchase-orders.csv" target="_blank" download="purchase-orders.csv">Download the following
            file, click 'Configure' on Integration Template for <b>Purchase orders</b>.</a>
        </div>
        <div class="template">
          <a href="/templates/purchase-order-items.csv" target="_blank" download="purchase-order-items.csv">Download the
            following file, click 'Configure' on Integration Template for <b>Purchase order items</b>.</a>
        </div>
      </div>
    </section>
    <section class="settings">
      <h2>Settings</h2>
      <p>Configure your warehouse sync settings.</p>
      <form name="config" @submit=${this._submit}>
        <label class="checkbox">
          Do you want to sync products from Shopify to your Warehouse?
          <input type="checkbox" name="syncProducts" ?checked=${ifDefined(this.config?.syncProducts)}>
        </label>
        <label class="checkbox">
          Do you want to periodically sync enriched product information from Shopify to your Warehouse?
          <input type="checkbox" name="syncProductEnrichments" ?checked=${ifDefined(this.config?.syncProductEnrichments)}>
        </label>
        <label class="checkbox">
          Do you want to sync orders from Shopify to your Warehouse?
          <input type="checkbox" name="syncOrders" ?checked=${ifDefined(this.config?.syncOrders)}>
        </label>
        <label class="checkbox">
          Do you want to sync inventory levels from your Warehouse to Shopify?
          <input type="checkbox" name="syncInventory" ?checked=${ifDefined(this.config?.syncInventory)}>
        </label>
        <label class="checkbox">
          <a href="/blogs/peak-mode" target="_blank">Are you entering a peak sales period</a>?
          <input type="checkbox" name="isPeakMode" ?checked=${ifDefined(this.config?.isPeakMode)}>
        </label>
        <label class="checkbox">
          Do you want to sync fulfillments (orders & transfer orders) from your Warehouse to Shopify?
          <input type="checkbox" name="syncFulfillments" ?checked=${ifDefined(this.config?.syncFulfillments)}>
        </label>
        <label class="checkbox">
          Do you want to sync transfer orders from Shopify to your Warehouse?
          <input type="checkbox" name="syncTransfers" ?checked=${ifDefined(this.config?.syncTransfers)}>
        </label>
        <label class="checkbox" hidden>
          Do you want to sync returns from your Warehouse to Shopify?<br>
          Do not enable this sync if you are using a third party returns provider.
          <input type="checkbox" name="syncReturns" ?checked=${ifDefined(this.config?.syncReturns)}>
        </label>
        <label class="checkbox" hidden>
          Do you want to fully refund shipping when refunding returns?
          <input type="checkbox" name="shouldFullyRefundReturnShipping" ?checked=${ifDefined(this.config?.shouldFullyRefundReturnShipping)}>
        </label>
        <label>
          Choose the location that represents your warehouse
          <select name="locationID" required>
            <option value="" hidden>Select a location</option>
            ${this.locations.map((location) => html`
            <option value="${location.id}" ?selected=${SetupPage._isSelected(`${location.id}`, this.config?.locationID)}>
              ${location.name}</option>`)}
          </select>
        </label>
        <label>
          Warehouse Url
          <input name="warehouseUrl" type="url" required placeholder="Example: https://wms.peoplevox.net/XXXXXXXXX"
            .value=${ifDefined(this.config?.warehouseUrl)}>
        </label>
        <label>
          Warehouse Username
          <input name="warehouseUsername" required placeholder="Example: sync user"
            .value=${ifDefined(this.config?.warehouseUsername)}>
        </label>
        <label>
          Warehouse Password
          <input name="warehousePassword" type="password" ?required=${!this.config?.warehouseUsername}
            placeholder="Example: XXXXXXXXX">
          <small>We will never display your password here for improved security, if you want to set or update it please fill
            out this value. Otherwise it will remain as the password you've previously set and you may leave this field
            blank.</small>
          <br>
        </label>
        <label class="checkbox">
          Do you want to sync to the PeopleVox TestSite only?<br>(Useful when testing Warehouse configuration)
          <input type="checkbox" name="useTestSite" ?checked=${ifDefined(this.config?.warehouseUseTestSite)}>
        </label>
        <details>
          <summary>Advanced</summary>
          <label>
            Choose how your products are grouped in PeopleVox
            <small>If you wish to group by category you must enable the enriched product information.</small>
            <select name="groupProductsByField" required>
              <option value="" hidden>Select a field</option>
              ${fields.map((field) => html`
              <option value="${field.id}" ?selected=${SetupPage._isSelected(field.id, this.config?.groupProductsByField)}>
                ${field.name}</option>`)}
            </select>
          </label>
          <label>
            Warehouse Site Reference
            <small>If you wish to use a different site from the Primary. Please contact us before changing this.</small>
            <input name="warehousePrimarySiteName" required placeholder="Example: PrimarySite"
              .value=${ifDefined(this.config?.warehousePrimarySiteName)}>
          </label>
        </details>
        <div class="buttons">
          <button type="submit">Save</button>
          <button type="reset">Clear</button>
        </div>
        <div class="status">
          <p ?hidden=${!this.isSavingComplete}>Settings saved.</p>
          <p ?hidden=${!this.isSavingError}>Error saving your settings.</p>
          <p ?hidden=${!this.isSaving}>Saving settings...</p>
        </div>
      </form>
    </section>
    <section class="billing">
      <h2>Billing</h2>
      <billing-section .service=${this.service}></billing-section>
    </section>
    <section class="validation">
      <h2>Product Validation</h2>
      <p>We can validate your products and email you the results.</p>
      <p>It is critial your products are valid so they can be imported into PeopleVox. They must have unique SKUs and
        Barcodes.</p>
      <validation-section .service=${this.service}></validation-section>
    </section>
    <section class="sync">
      <h2>Manual Sync</h2>
      <p>This app listens for changes and syncs automatically. However you many want to manually sync items to your warehouse
        occasionally, for example for the first time after setup.</p>
      <p>You must ensure your products are valid before running this using our validation tool above.</p>
      <p>Click to manually start a product sync job.</p>
      <product-sync-section .service=${this.service} .email=${this.email}></product-sync-section>
    </section>
    <section class="integrations">
      <h2>Integrations</h2>
      <inventory-planner .service=${this.service}></inventory-planner>
    </section>
    `;
  }

  /**
   * Defines the elements properties
   *
   * @returns {object} the elements properties
   */
  static get properties() {
    return {
      /** Used to interact with api */
      service: {type: Object},
      /** Auth email address */
      email: {type: String},
      /** If we are loading data */
      isLoading: {type: Boolean},
      /** If there is an error loading data*/
      isError: {type: Boolean},
      /** If we are saving data */
      isSaving: {type: Boolean},
      /** If there is an error saving data*/
      isSavingError: {type: Boolean},
      /** If we have saved data */
      isSavingComplete: {type: Boolean},
      /** The shopify locations */
      locations: {type: Array},
      /** The sync config */
      config: {type: Object},
    };
  }

  /**
   * Defines the elements properties
   *
   * @returns {object} the elements properties
   */
  constructor() {
    super();
    const analytics = new Analytics();
    const auth = new Auth(analytics);
    const service = new Service(auth);
    this.service = service;
    this.email = '';
    this.isLoading = false;
    this.isError = false;
    this.isSaving = false;
    this.isSavingError = false;
    this.isSavingComplete = false;
    this.locations = [];
    this.config = {};
  }

  /**
   * Fired whenever any property changes and the template updates
   *
   * @param {PropertyValues} changedProperties map of changed properties
   */
  updated(changedProperties) {
    if (super.updated) {
      super.updated(changedProperties);
    }

    if (changedProperties.has('service') &&
      this.service?.getConfig
    ) {
      this._load();
    }
  }

  /**
   * Load the settings for a store
   */
  async _load() {
    this.isError = false;
    this.isLoading = true;
    try {
      const configLoad = this.service.getConfig();
      const locationsLoad = this.service.getLocations();

      const [config, locations] = await Promise.all([configLoad, locationsLoad]);
      this.config = config;
      this.locations = locations;
    } catch (e) {
      this.isError = true;
    } finally {
      this.isLoading = false;
    }
  }

  /**
   * Handle form submission updating the store settings
   *
   * @param {Event} event the submission event
   */
  async _submit(event) {
    event.preventDefault();
    this.isSaving = false;
    this.isSavingError = false;
    const form = this.querySelector('form[name="config"]');
    const valid = form.reportValidity();
    if (valid) {
      try {
        this.isSaving = true;
        const data = getFormJSON(form);
        await this.service.upsertConfig({
          ...data,
          syncProducts: !!data.syncProducts,
          syncOrders: !!data.syncOrders,
          syncTransfers: !!data.syncTransfers,
          syncInventory: !!data.syncInventory,
          syncFulfillments: !!data.syncFulfillments,
          syncReturns: !!data.syncReturns,
          shouldFullyRefundReturnShipping: !!data.shouldFullyRefundReturnShipping,
          syncProductEnrichments: !!data.syncProductEnrichments,
          useTestSite: !!data.useTestSite,
          isPeakMode: !!data.isPeakMode,
        });
        this.isSaving = false;
        this.isSavingComplete = true;
      } catch (e) {
        this.isSaving = false;
        this.isSavingError = true;
      } finally {
        await new Promise((resolve) => setTimeout(resolve, 3000));
        this.isSaving = false;
        this.isSavingError = false;
        this.isSavingComplete = false;
      }
    }
  }

  /**
   * If the option is the selected on the dropdown
   *
   * @param {string} option the option
   * @param {string} value the value
   * @returns {boolean} if selected
   */
  static _isSelected(option, value) {
    return option === value;
  }
}
window.customElements.define('setup-page', SetupPage);
